import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Snowflake } from '@sorare/core/src/atoms/icons/Snowflake';
import { Trophy } from '@sorare/core/src/atoms/icons/Trophy';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/lib/gql';

import { SetCollectionTask } from '#src/components/missions/SetCollectionTask/index.tsx';
import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';

import type { CollectionMissions_currentUser } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
`;

const Section = styled(Vertical).attrs({ gap: 1.5 })`
  width: 100%;
`;

const SectionTitle = styled(BrandS)`
  display: flex;
  align-items: center;
  gap: var(--unit);
  & svg {
    color: var(--c-set-surface-primary);
  }
`;

const hasCompetition = (
  task: NonNullable<
    CollectionMissions_currentUser['setCollectionTasks']
  >[number]
) => {
  if (
    !isType(task, 'CardsCountTask') &&
    !isType(task, 'CompleteCollectionsCountTask')
  ) {
    return false;
  }
  return !!task.competition?.slug;
};

type Props = {
  currentUser: Nullable<CollectionMissions_currentUser>;
  onRefetch?: () => void;
  onTaskClick: (taskId: string) => void;
  selectedTaskId?: Nullable<string>;
};

export const CollectionMissions = ({
  currentUser,
  onRefetch,
  onTaskClick,
  selectedTaskId,
}: Props) => {
  const {
    onClaim,
    loadingTaskId,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  const { setCollectionTasks } = currentUser || {};

  if (!setCollectionTasks?.length) {
    return null;
  }

  const claimNowTasks = setCollectionTasks.filter(
    task => task.aasmState === ManagerProgressionTaskState.COMPLETED
  );

  const inProgressLeagueTasks = setCollectionTasks.filter(
    task =>
      (task.aasmState === ManagerProgressionTaskState.READY ||
        task.aasmState === ManagerProgressionTaskState.ASSIGNED) &&
      hasCompetition(task)
  );

  const inProgressGeneralTasks = setCollectionTasks.filter(
    task =>
      (task.aasmState === ManagerProgressionTaskState.READY ||
        task.aasmState === ManagerProgressionTaskState.ASSIGNED) &&
      !hasCompetition(task)
  );

  const completedTasks = setCollectionTasks.filter(
    task => task.aasmState === ManagerProgressionTaskState.CLAIMED
  );

  const renderTasks = (tasks: typeof setCollectionTasks) =>
    tasks.map(task => (
      <SetCollectionTask
        key={task.id}
        task={task}
        onClaim={async () => onClaim(task)}
        loading={loadingTaskId === task.id}
        onClick={() => onTaskClick(task.id)}
        selected={selectedTaskId === task.id}
      />
    ));

  return (
    <>
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
          onRefetch?.();
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
          onRefetch?.();
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
          onRefetch?.();
        }}
      />
      <Root>
        {claimNowTasks.length > 0 && (
          <Section>
            <SectionTitle>
              <FontAwesomeIcon
                icon={iconGift}
                color="var(--c-set-surface-primary)"
              />
              <FormattedMessage defaultMessage="Claim Now" />
            </SectionTitle>
            {renderTasks(claimNowTasks)}
          </Section>
        )}
        {inProgressLeagueTasks.length > 0 && (
          <Section>
            <SectionTitle>
              <Trophy size={2} />
              <FormattedMessage defaultMessage="League" />
            </SectionTitle>
            {renderTasks(inProgressLeagueTasks)}
          </Section>
        )}
        {inProgressGeneralTasks.length > 0 && (
          <Section>
            <SectionTitle>
              <Snowflake size={2} />
              <FormattedMessage defaultMessage="General" />
            </SectionTitle>
            {renderTasks(inProgressGeneralTasks)}
          </Section>
        )}
        {completedTasks.length > 0 && (
          <Section>
            <SectionTitle>
              <FontAwesomeIcon
                icon={iconCheck}
                color="var(--c-set-surface-primary)"
              />
              <FormattedMessage defaultMessage="Completed" />
            </SectionTitle>
            {renderTasks(completedTasks)}
          </Section>
        )}
      </Root>
    </>
  );
};

CollectionMissions.fragments = {
  currentUser: gql`
    fragment CollectionMissions_currentUser on CurrentUser {
      slug
      setCollectionTasks(sport: $sport) {
        id
        ... on CardsCountTask {
          id
          competition {
            slug
          }
        }
        ... on CompleteCollectionsCountTask {
          id
          competition {
            slug
          }
        }
        ...SetCollectionTask_taskInterface
        ...TaskClaimedDialogs_taskInterface
      }
    }
    ${SetCollectionTask.fragments.taskInterface}
    ${TaskClaimedDialogs.fragments.taskInterface}
  ` as TypedDocumentNode<CollectionMissions_currentUser>,
};
